import { withWidth } from '@material-ui/core';
import { Alert, AlertTitle, alpha, Box, Breakpoint, Card, CardContent, Grid, ToggleButton, Typography, useTheme } from '@mui/material';
import React from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { IParlayOutcome, IShopOutcome, Leagues, Operators } from '../pick6/store/types';
import { IRootState } from '../../store/reducer';
import { addLeg, removeLeg, setAdditionalFiltersLeague, setAdditionalLegs, swapLegs } from './store/slice';
import { getISOStringsForTimeframe, getLeague } from '../pick6/optimizerHelper';
import { getSportsbookCodeLabel, IAdditionalParlayLegsFilters, LeagueDisplayText } from './store/types';
import { Timeframe } from './TimeFrameDropdown';
import { APIConstants } from '../utils/api/constants';
import { getAdditionalLegs } from './store/actions';
import SelectComponent from './Select';
import { data as leagueData } from './store/data';

import RefreshIcon from '@mui/icons-material/Refresh';
import MatchupsWithCheckboxComponent from './MatchupsWithCheckboxComponent';
import OffersFilter from './OffersFilter';
import AdditionalLegsTable from './AdditionalLegsTable';

interface OwnProps {
  isDisplayUnlocked?: boolean;
}

type Props = OwnProps & { width: Breakpoint };

const PAGE_SIZE: number = 50;

const AdditionalLegs = (props: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { isDisplayUnlocked } = props;
  const [rawData, setRawData] = React.useState<IShopOutcome[]>([]);
  const page = React.useRef(1);
  const loadingForInfiniteScroll = React.useRef(false);
  const [infiniteScrollHeight, setInfiniteScrollHeight] = React.useState(700);
  const [rowCount, setRowCount] = React.useState(0);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: PAGE_SIZE,
    page: 0,
  });

  const { width } = props;
  const {
    selectedLegs,
    additionalLegs,
    league,
    state,
    timeframe,
    sportsbook,
    projectionSystem,
    additionalParlayLegFilters,
    operator,
  } = useSelector((state: IRootState) => state.pickemOptimizer);

  // When the additional legs api call changes, add that to the raw data and determine if we reached the end of the data or not
  React.useEffect(() => {
    if (additionalLegs?.status === APIConstants.SUCCEEDED) {
      if (page.current === 1) {
        setRawData(additionalLegs?.data.outcomes);
      } else {
        setRawData(rawData.concat(additionalLegs?.data.outcomes));
      }
    }
    loadingForInfiniteScroll.current = false;
  }, [additionalLegs]);

  // When the filters or selected legs change, we should reset the data in the infinite scroll. We chose not to use extra state to purposefully
  // increase the number of renders. We aren't using web sockets, so the more renders, the better the data integrity
  React.useEffect(() => {
    page.current = 1;
    getOutcomes(
      additionalParlayLegFilters,
      paginationModel.page + 1,
      // selectedLegs,
      [],
      sportsbook,
    );
  }, [additionalParlayLegFilters, selectedLegs, state, timeframe, paginationModel, isDisplayUnlocked, operator]);

  React.useEffect(() => {
    const legsDrawerRef = document.getElementById('legs-drawer-mobile');
    if (legsDrawerRef?.clientHeight && operator !== Operators.NONE) {
      setInfiniteScrollHeight(legsDrawerRef?.clientHeight - 145);
    }
  });

  const getOutcomes = async (
    additionalFilters: IAdditionalParlayLegsFilters,
    currentPage: number,
    currentLegs: IParlayOutcome[],
    sportsbookCode?: string,
  ) => {
    const timeframes = getISOStringsForTimeframe(timeframe as Timeframe);
    console.log(additionalFilters)
    const config = {
      leagueCode:
        additionalFilters.league === Leagues.ALL
          ? ''
          : additionalFilters.league,
      state: state.abbreviation|| 'IL',
      events: additionalFilters.events
        ? additionalFilters.events.join(',')
        : '',
      offers: additionalFilters.betTypes
        ? additionalFilters.betTypes.map(x => x.id).join(',')
        : '',
      operator: operator ?? Operators.NONE,
      sportsbookCode: sportsbookCode
        ? getSportsbookCodeLabel(sportsbookCode)
        : undefined,
      projectionSystem,
      currentPage,
      pageSize: paginationModel.pageSize,
      startDate: timeframes?.startISOString,
      endDate: timeframes?.endISOString,
      sortBy: operator && operator !== Operators.NONE ? 'win' : 'sev',
      selectedLegs: currentLegs ? currentLegs?.map((l) => l.hashCode).join(',') : '',
    };

    const additionalLegs = await getAdditionalLegs({ ...config }, isDisplayUnlocked || false);

    dispatch(setAdditionalLegs(additionalLegs));
  };

  const getType = (o: IShopOutcome): 'Add' | 'Swap' | 'Remove' => {
    if (
      selectedLegs
      // Standard Parlay Add Cases
      && ((operator !== Operators.NONE
        && selectedLegs.map((l) => l.playerId).indexOf(o.playerId) === -1))
    ) {
      return 'Add';
    } else if (selectedLegs
      // Standard Parlay Add Cases
      && ((operator !== Operators.NONE
        && selectedLegs.map((l) => l.outcomeId).indexOf(o.outcomeId) > -1))) {
      return 'Remove';
    }
    return 'Swap';
  };

  const handleClose = () => {
  };

  const handleLegClick = (o: IParlayOutcome) => {
    if (
      selectedLegs
      && ((operator !== Operators.NONE
        && selectedLegs.map((l) => l.playerId).indexOf(o.playerId) === -1))
    ) {
      dispatch(addLeg(o));
    } else if (selectedLegs
      // Standard Parlay Add Cases
      && ((operator !== Operators.NONE && selectedLegs.map((l) => l.outcomeId).indexOf(o.outcomeId) > -1))) {
      dispatch(removeLeg(o));
    } else {
      dispatch(swapLegs(o));
    }
  };

  const handleLeague = (newLeague: string) => {
    dispatch(setAdditionalFiltersLeague(getLeague(newLeague)));
    handleClose();
  };

  React.useEffect(() => {
    setRowCount(additionalLegs?.data?.pagination?.totalCount || 0)
  }, [additionalLegs])

  return (
    <Box sx={{
      height: '100%',
      overflowY: 'hidden',
      overflowX: 'hidden',
      [theme.breakpoints.down('sm')]: {
        padding: 0,
      },
    }}>
      <Grid container spacing={2} sx={{ height: '100%', bgcolor: '#101010', }}>
        <Grid item xs={12}>
          <Card sx={{
            padding: 0,
            flexGrow: 1,
            borderRadius: '10px',
            backgroundColor: '#101010',
            height: '100%',
            '& .MuiCardHeader-root': {
              boxShadow: theme.shadows[3],
            },
            '& .MuiCardHeader-title': {
              fontWeight: 600,
              fontSize: '1.25rem',
            },
            '& .MuiCardContent-root': {
              backgroundColor: '#101010',
              [theme.breakpoints.down('sm')]: {
                padding: '6px !important',
              }
            }
          }}>
            <CardContent>
              <Grid item xs={12} mb={2}>
                <Grid
                  container
                  spacing={2}
                  id="back-to-top-anchor"
                  sx={{
                    marginBottom: '16px',
                    [theme.breakpoints.down('xs')]: {
                      marginBottom: 0,
                    },
                  }}
                >
                  <Grid item xs={12} sm={12} lg={12} sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex',
                    justifyContent: 'space-between',
                    [theme.breakpoints.down('sm')]: {
                      flexDirection: 'column',
                      justifyContent: 'start',
                    },
                  }}>
                    <Typography variant="h6" sx={{
                      fontSize: '24px !important',
                      fontWeight: 700,
                      lineHeight: 1,
                      marginRight: '8px',
                      color: '#4395ff',
                      [theme.breakpoints.down('xs')]: {
                        fontSize: '2rem',
                      },
                    }}>
                      Add Picks To Your Entry
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} lg={2}>
                  <SelectComponent
                    backgroundColorOverride={'#1C1C1C'}
                    options={leagueData.leagues}
                    label={league === Leagues.ALL
                      ? 'All Leagues'
                      : LeagueDisplayText(league)}
                    getMenuItemLabel={(item) => item.label}
                    getValue={(item) => item.value}
                    handleClick={handleLeague}
                  />
                </Grid>
                <Grid item xs={6} sm={4} lg={2}>
                  <MatchupsWithCheckboxComponent />
                </Grid>
                <Grid item xs={6} sm={4} lg={2}>
                  <OffersFilter />
                </Grid>
                <Grid item xs={1}>
                  <Box sx={{
                    height: '100%',
                    width: '40px',
                    '& button': {
                      height: '100%',
                      padding: '6px',
                      borderRadius: '6px',
                      color: theme.palette.common.white,
                      backgroundColor: '#1C1C1C',
                      // [theme.breakpoints.down('sm')]: {
                      //   width: '30px',
                      //   height: '30px',
                      // }
                    },
                  }}>
                    <ToggleButton value="" onClick={() => getOutcomes(
                      additionalParlayLegFilters,
                      page.current,
                      // selectedLegs,
                      [],
                      sportsbook,
                    )}>
                      <RefreshIcon />
                    </ToggleButton>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
            <CardContent
              sx={{
                width: '100%',
                paddingTop: 0,
                [theme.breakpoints.down('xs')]: {
                  '& .MuiCardContent-root': {
                    paddingTop: 0,
                    padding: '0 !important',
                  }
                },
              }}
              style={{
                height: width === 'xs' ? infiniteScrollHeight + 50 : 725,
              }}
            >
              {additionalLegs.status === APIConstants.SUCCEEDED
                && rawData.length === 0 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box mt={3} mb={2}>
                        <Alert icon={false} variant="outlined" severity="info" sx={{ border: `1px solid ${theme.palette.primary.main}`, borderRadius: '12px', fontWeight: 500, color: alpha(theme.palette.common.white, .7) }}>
                          <AlertTitle sx={{ fontWeight: 700, color: 'white' }}>No additional legs available</AlertTitle>
                          We couldn't find any legs to add based on your current
                          selections. Please update your selections to see a new
                          parlay and legs to add.
                        </Alert>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              <AdditionalLegsTable
                isPickemTable={true}
                width={width}
                additionalLegs={additionalLegs}
                getType={getType}
                handleLegClick={handleLegClick}
                isDisplayUnlocked={isDisplayUnlocked || false}
                defaultSort={
                  (() => {
                    if (operator === Operators.NONE) { return 'sev' }
                    else { return 'expectedWinPct'; }
                  })()
                }
                tableProps={{
                  pagination: true,
                  paginationMode: 'server',
                  rowCount: rowCount,
                  paginationModel: {
                    pageSize: paginationModel.pageSize,
                    page: paginationModel.page
                  },
                  onPaginationModelChange: (model) => {
                    setPaginationModel({
                      page: model.page,
                      pageSize: model.pageSize,
                    })
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default withWidth()(AdditionalLegs);